import { useState, useEffect } from 'react';
import { api, formatPrice } from '@/lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Package, Send, ExternalLink, CheckCircle2, Clock, Check, X } from 'lucide-react';
import { toast } from 'sonner';

const statusOptions = [
    { value: 'pending', label: 'Pendiente', color: 'bg-cp-warning/10 text-cp-warning' },
    { value: 'confirmed', label: 'Confirmado', color: 'bg-cp-secondary-soft text-cp-secondary' },
    { value: 'shipped', label: 'Enviado', color: 'bg-cp-primary-soft text-cp-brand' },
    { value: 'delivered', label: 'Entregado', color: 'bg-cp-success/10 text-cp-success' },
    { value: 'cancelled', label: 'Cancelado', color: 'bg-cp-error/10 text-cp-error' },
];

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');

    const load = async () => {
        try {
            const params = filter !== 'all' ? { status: filter } : {};
            const r = await api.get('/admin/orders', { params });
            setOrders(r.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    };
    useEffect(() => { load(); }, [filter]);

    const updateStatus = async (orderId, newStatus) => {
        try {
            await api.put(`/admin/orders/${orderId}/status`, { status: newStatus });
            toast.success('Estado actualizado');
            load();
        } catch { toast.error('Error al actualizar'); }
    };

    const [confirmingPayment, setConfirmingPayment] = useState({});
    const updatePaymentStatus = async (orderId, status) => {
        setConfirmingPayment(p => ({ ...p, [orderId]: status }));
        try {
            await api.put(`/admin/orders/${orderId}/payment-status`, { payment_status: status });
            toast.success(status === 'paid' ? 'Pago confirmado y cliente notificado' : 'Pago rechazado y cliente notificado');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al actualizar pago');
        }
        setConfirmingPayment(p => ({ ...p, [orderId]: null }));
    };

    const [sendingWpp, setSendingWpp] = useState({});
    const sendWhatsapp = async (orderId) => {
        setSendingWpp(p => ({ ...p, [orderId]: true }));
        try {
            await api.post(`/admin/orders/${orderId}/send-whatsapp`);
            toast.success('PDF enviado por WhatsApp');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al enviar');
        }
        setSendingWpp(p => ({ ...p, [orderId]: false }));
    };

    const getStatusInfo = (s) => statusOptions.find(o => o.value === s) || statusOptions[0];

    return (
        <div className="space-y-6" data-testid="admin-orders">
            <div className="flex items-center justify-between flex-wrap gap-4">
                <h1 className="text-2xl font-heading font-bold text-cp-text-main">Pedidos</h1>
                <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-44" data-testid="orders-filter">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            <div className="bg-white border border-cp-border rounded-xl overflow-hidden">
                {orders.length === 0 ? (
                    <div className="p-12 text-center text-cp-text-muted">
                        <Package size={32} className="mx-auto mb-2 opacity-50" />
                        <p>No hay pedidos{filter !== 'all' ? ` con estado "${getStatusInfo(filter).label}"` : ''}</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="orders-table">
                            <thead><tr className="border-b border-cp-border-soft bg-cp-bg-alt">
                                <th className="text-left p-3 text-cp-text-muted font-medium">Pedido</th>
                                <th className="text-left p-3 text-cp-text-muted font-medium">Cliente</th>
                                <th className="text-left p-3 text-cp-text-muted font-medium hidden md:table-cell">Productos</th>
                                <th className="text-right p-3 text-cp-text-muted font-medium">Total</th>
                                <th className="text-center p-3 text-cp-text-muted font-medium hidden lg:table-cell">Pago</th>
                                <th className="text-left p-3 text-cp-text-muted font-medium hidden sm:table-cell">Fecha</th>
                                <th className="text-center p-3 text-cp-text-muted font-medium">Estado</th>
                                <th className="text-center p-3 text-cp-text-muted font-medium">WPP</th>
                            </tr></thead>
                            <tbody>
                                {orders.map(o => {
                                    const si = getStatusInfo(o.status);
                                    const isPaid = o.payment_status === 'paid';
                                    return (
                                        <tr key={o.id} className="border-b border-cp-border-soft last:border-0 hover:bg-cp-bg-alt/50">
                                            <td className="p-3">
                                                <p className="font-mono text-xs text-cp-text-body">#{o.id?.slice(0,8)}</p>
                                            </td>
                                            <td className="p-3">
                                                <p className="font-medium text-cp-text-main">{o.customer_name}</p>
                                                <p className="text-xs text-cp-text-muted">{o.customer_phone}</p>
                                            </td>
                                            <td className="p-3 hidden md:table-cell">
                                                <p className="text-cp-text-body text-xs">{o.items?.length || 0} producto(s)</p>
                                            </td>
                                            <td className="p-3 text-right font-semibold text-cp-brand">{formatPrice(o.total)}</td>
                                            <td className="p-3 hidden lg:table-cell">
                                                <div className="flex flex-col items-center gap-1.5">
                                                    {o.payment_status === 'paid' && (
                                                        <span className="flex items-center gap-1 text-xs text-cp-success font-semibold">
                                                            <CheckCircle2 size={12} />Pagado
                                                        </span>
                                                    )}
                                                    {o.payment_status === 'rejected' && (
                                                        <span className="flex items-center gap-1 text-xs text-cp-error font-semibold">
                                                            <X size={12} />Rechazado
                                                        </span>
                                                    )}
                                                    {(!o.payment_status || o.payment_status === 'pending') && (
                                                        <span className="flex items-center gap-1 text-xs text-cp-text-muted">
                                                            <Clock size={12} />Pendiente
                                                        </span>
                                                    )}
                                                    {o.payment_status !== 'paid' && o.payment_status !== 'rejected' ? (
                                                        <div className="flex gap-1 mt-0.5">
                                                            <button
                                                                onClick={() => updatePaymentStatus(o.id, 'paid')}
                                                                disabled={!!confirmingPayment[o.id]}
                                                                title="Confirmar pago"
                                                                className="flex items-center gap-0.5 px-2 py-1 rounded-lg bg-cp-success/10 hover:bg-cp-success/20 text-cp-success text-xs font-semibold disabled:opacity-40 transition-colors"
                                                            >
                                                                {confirmingPayment[o.id] === 'paid'
                                                                    ? <span className="w-3 h-3 border-2 border-cp-success border-t-transparent rounded-full animate-spin" />
                                                                    : <Check size={11} />
                                                                }
                                                                OK
                                                            </button>
                                                            <button
                                                                onClick={() => updatePaymentStatus(o.id, 'rejected')}
                                                                disabled={!!confirmingPayment[o.id]}
                                                                title="Rechazar pago"
                                                                className="flex items-center gap-0.5 px-2 py-1 rounded-lg bg-cp-error/10 hover:bg-cp-error/20 text-cp-error text-xs font-semibold disabled:opacity-40 transition-colors"
                                                            >
                                                                {confirmingPayment[o.id] === 'rejected'
                                                                    ? <span className="w-3 h-3 border-2 border-cp-error border-t-transparent rounded-full animate-spin" />
                                                                    : <X size={11} />
                                                                }
                                                                No
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            onClick={() => updatePaymentStatus(o.id, o.payment_status === 'paid' ? 'rejected' : 'paid')}
                                                            disabled={!!confirmingPayment[o.id]}
                                                            className="text-xs text-cp-text-muted hover:text-cp-text-body underline disabled:opacity-40"
                                                        >
                                                            Cambiar
                                                        </button>
                                                    )}
                                                    {o.payment_url && (
                                                        <a href={o.payment_url} target="_blank" rel="noreferrer"
                                                            className="flex items-center gap-0.5 text-xs text-cp-brand hover:underline">
                                                            <ExternalLink size={10} />Link
                                                        </a>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-3 text-cp-text-muted text-xs hidden sm:table-cell">{o.created_at?.slice(0,10)}</td>
                                            <td className="p-3">
                                                <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                                                    <SelectTrigger className={`h-8 text-xs font-semibold border-0 ${si.color}`} data-testid={`order-status-${o.id}`}>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                                                    </SelectContent>
                                                </Select>
                                            </td>
                                            <td className="p-3 text-center">
                                                <button
                                                    onClick={() => sendWhatsapp(o.id)}
                                                    disabled={sendingWpp[o.id]}
                                                    title="Enviar PDF por WhatsApp"
                                                    data-testid={`send-wpp-${o.id}`}
                                                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366] disabled:opacity-40 transition-colors"
                                                >
                                                    {sendingWpp[o.id]
                                                        ? <span className="w-3 h-3 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
                                                        : <Send size={13} />
                                                    }
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
